import axios from 'axios';
import { hostUI } from '../../../../configuraciones';
import React, { useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import Swal from "sweetalert2";

// Material UI Icons
import GrassIcon from '@mui/icons-material/Grass';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import CancelIcon from '@mui/icons-material/Cancel';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import SearchIcon from '@mui/icons-material/Search';
import InboxIcon from '@mui/icons-material/Inbox';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ScienceIcon from '@mui/icons-material/Science';

export const TIPOS_HORNO = [
  "Horno de pozo",
  "Horno de mampostería",
  "Autoclave"
];

const renderCoccionStatusBadge = (status) => {
  const norm = String(status || 'borrador').toLowerCase().trim();
  if (norm === 'aceptada' || norm === 'aceptado' || norm === 'aprobado' || norm === 'aprobada' || norm === 'aprobado_ui') {
    return <span className="badge-status aceptada">Aceptada</span>;
  }
  if (norm === 'en_revision' || norm === 'revision') {
    return <span className="badge-status revision">En Revisión</span>;
  }
  if (norm === 'enviada' || norm === 'enviado' || norm === 'nueva' || norm === 'pendiente' || norm === 'pendiente_ui') {
    return <span className="badge-status enviada">Enviada</span>;
  }
  if (norm === 'rechazada' || norm === 'rechazado' || norm === 'rechazado_ui') {
    return <span className="badge-status rechazada">Rechazada</span>;
  }
  if (norm === 'cancelada' || norm === 'cancelado') {
    return <span className="badge-status cancelada">Cancelada</span>;
  }
  if (norm === 'borrador' || norm === 'borrador_ui') {
    return <span className="badge-status borrador">Borrador</span>;
  }
  return <span className="badge-status borrador">Borrador</span>;
};

const renderModalHeaderStatusBadge = (status) => {
  const norm = String(status || 'borrador').toLowerCase().trim();
  let label = "Borrador";
  let bg = "rgba(255,255,255,0.25)";
  if (norm === 'aceptada' || norm === 'aceptado' || norm === 'aprobado' || norm === 'aprobada' || norm === 'aprobado_ui') {
    label = "Aceptada";
    bg = "#059669";
  } else if (norm === 'en_revision' || norm === 'revision') {
    label = "En Revisión";
    bg = "#D97706";
  } else if (norm === 'enviada' || norm === 'enviado' || norm === 'nueva' || norm === 'pendiente' || norm === 'pendiente_ui') {
    label = "Enviada";
    bg = "#2563EB";
  } else if (norm === 'rechazada' || norm === 'rechazado' || norm === 'rechazado_ui') {
    label = "Rechazada";
    bg = "#DC2626";
  } else if (norm === 'cancelada' || norm === 'cancelado') {
    label = "Cancelada";
    bg = "#64748B";
  }
  return (
    <span style={{ fontSize: "11.5px", padding: "3px 10px", borderRadius: "12px", background: bg, color: "#FFFFFF", fontWeight: 700, border: "1px solid rgba(255,255,255,0.4)" }}>
      {label}
    </span>
  );
};


export const getCoccionMoliendaBalance = (coccion, moliendaRecords = [], currentMoliendaId = null) => {
  if (!coccion) return { totalKgCocidos: 0, kgUsados: 0, kgDisponibles: 0, isAgotada: true, pctConsumido: 0, moliendasCount: 0 };

  const totalKgCocidos = Number(
    String(coccion.kgMagueyCocido || coccion.kg_maguey_cocido || coccion.kgMaguey || coccion.kg_maguey || 0)
      .replace(/[^\d.]/g, '')
  ) || 0;

  const noProd = String(coccion.noProduccion || coccion.no_produccion || '').trim();
  const coccionId = String(coccion.rawId || coccion.id || coccion.id_bitacora_coccion || '').trim();

  const matchingMoliendas = (Array.isArray(moliendaRecords) ? moliendaRecords : []).filter(m => {
    if (currentMoliendaId && String(m.rawId || m.id || m.id_bitacora_molienda) === String(currentMoliendaId)) {
      return false;
    }
    const isCancelled = String(m.status || m.estatus || '').toLowerCase().includes('cancelad');
    if (isCancelled) return false;

    const mNoProd = String(m.noProduccion || m.no_produccion || '').trim();
    const mCoccionId = String(m.id_bitacora_coccion || m.idBitacoraCoccion || '').trim();

    return (noProd && mNoProd === noProd) || (coccionId && mCoccionId === coccionId);
  });

  const kgUsados = matchingMoliendas.reduce((acc, m) => {
    const items = Array.isArray(m.items) ? m.items : [];
    const moliendaKg = items.reduce((tAcc, it) => tAcc + (Number(String(it.kgMaguey || it.kg_maguey || 0).replace(/[^\d.]/g, '')) || 0), 0);
    const fallbackKg = Number(String(m.totalKgMaguey || m.total_kg_maguey || 0).replace(/[^\d.]/g, '')) || 0;
    return acc + (moliendaKg > 0 ? moliendaKg : fallbackKg);
  }, 0);

  const kgDisponibles = Math.max(0, totalKgCocidos - kgUsados);
  const isAgotada = totalKgCocidos > 0 ? (kgDisponibles <= 0) : false;
  const pctConsumido = totalKgCocidos > 0 ? Math.min(100, Math.round((kgUsados / totalKgCocidos) * 100)) : 0;

  return {
    totalKgCocidos,
    kgUsados,
    kgDisponibles,
    isAgotada,
    pctConsumido,
    moliendasCount: matchingMoliendas.length
  };
};

const CoccionSection = ({
  coccionRecords = [],
  moliendaRecords = [],
  pagination,
  coccionSearch = "",
  onSearchChange,
  onPageChange,
  statusFilter: propStatusFilter,
  onStatusFilterChange,
  isFormOpen,
  setIsFormOpen,
  formData = {},
  setFormData,
  magueySpecies = [],
  catalogoMaguey: propCatalogoMaguey = [],
  handleSaveCoccion,
  handleCancelCoccion,
  handleRejectCoccion,
  onOpenNewMolienda,
  instalaciones = []
}) => {
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [localStatusFilter, setLocalStatusFilter] = useState("todos");

  const [localCatalogo, setLocalCatalogo] = useState([]);

  useEffect(() => {
    if (Array.isArray(propCatalogoMaguey) && propCatalogoMaguey.length > 0) {
      setLocalCatalogo(propCatalogoMaguey);
    } else {
      axios.get(hostUI + "bitacora-produccion/catalogo-maguey")
        .then(res => {
          const list = res.data?.data || res.data || [];
          if (Array.isArray(list) && list.length > 0) setLocalCatalogo(list);
        })
        .catch(() => { });
    }
  }, [propCatalogoMaguey]);

  const activeCatalog = useMemo(() => {
    if (localCatalogo.length > 0) return localCatalogo;
    if (Array.isArray(propCatalogoMaguey) && propCatalogoMaguey.length > 0) return propCatalogoMaguey;
    return [
      { nc: 1, nombre_comun: "Espadín", nombre_cientifico: "Agave angustifolia Haw", edad_madurez_anios: 8 },
      { nc: 2, nombre_comun: "Tobalá", nombre_cientifico: "Agave potatorum Zucc", edad_madurez_anios: 12 },
      { nc: 3, nombre_comun: "Arroqueño", nombre_cientifico: "Agave americana L.", edad_madurez_anios: 20 },
      { nc: 4, nombre_comun: "Tepeztate", nombre_cientifico: "Agave marmorata", edad_madurez_anios: 25 },
      { nc: 5, nombre_comun: "Mexicano", nombre_cientifico: "Agave rhodacantha", edad_madurez_anios: 10 },
      { nc: 6, nombre_comun: "Madrecuixe / Cuishe", nombre_cientifico: "Agave karwinskii", edad_madurez_anios: 12 },
      { nc: 7, nombre_comun: "Jabalí", nombre_cientifico: "Agave convallis", edad_madurez_anios: 12 },
      { nc: 8, nombre_comun: "Chiquilao", nombre_cientifico: "Agave seemanniana", edad_madurez_anios: 10 }
    ];
  }, [localCatalogo, propCatalogoMaguey]);

  const firstMagueyObj = activeCatalog[0] || { nc: 1, nombre_comun: "Espadín", nombre_cientifico: "Agave angustifolia Haw", edad_madurez_anios: 8 };
  const firstMaguey = `${firstMagueyObj.nombre_cientifico} (${firstMagueyObj.nombre_comun})`;

  const statusFilter = propStatusFilter !== undefined ? propStatusFilter : localStatusFilter;
  const handleStatusFilterChange = (val) => {
    setLocalStatusFilter(val);
    if (onStatusFilterChange) onStatusFilterChange(val);
  };

  const effectiveInstalaciones = useMemo(() => {
    if (Array.isArray(instalaciones) && instalaciones.length > 0) {
      return instalaciones;
    }
    return [];
  }, [instalaciones]);

  const filteredRecords = useMemo(() => {
    if (propStatusFilter !== undefined) {
      return coccionRecords;
    }
    return coccionRecords.filter(rec => {
      if (statusFilter === "todos") return true;
      const norm = String(rec.status || "borrador").toLowerCase().trim();
      if (statusFilter === "aceptada") return norm === "aceptada" || norm === "aceptado" || norm === "aprobado" || norm === "aprobada" || norm === "aprobado_ui";
      if (statusFilter === "enviada") return norm === "enviada" || norm === "enviado" || norm === "revision" || norm === "en_revision" || norm === "nueva" || norm === "pendiente" || norm === "pendiente_ui";
      if (statusFilter === "rechazada") return norm === "rechazada" || norm === "rechazado" || norm === "rechazado_ui";
      if (statusFilter === "cancelada") return norm === "cancelada" || norm === "cancelado";
      if (statusFilter === "borrador") return norm === "borrador";
      return true;
    });
  }, [coccionRecords, statusFilter, propStatusFilter]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "idUnidadProduccion") {
      const foundInst = effectiveInstalaciones.find(i => String(i.idanexocliente || i.id) === String(value));
      const alias = foundInst?.informaciongeneralinstalacioncliente?.alias || foundInst?.alias || value;
      setFormData(prev => ({
        ...prev,
        idUnidadProduccion: value,
        unidadProduccion: alias
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Lista de especies de maguey en el formulario
  const formMagueyes = useMemo(() => {
    if (Array.isArray(formData?.magueyes) && formData.magueyes.length > 0) {
      return formData.magueyes.map(m => {
        let ncVal = m.nc || m.idMaguey || null;
        if (!ncVal && m.tipoMaguey) {
          const match = activeCatalog.find(c =>
            m.tipoMaguey.toLowerCase().includes(c.nombre_comun.toLowerCase()) ||
            m.tipoMaguey.toLowerCase().includes(c.nombre_cientifico.toLowerCase())
          );
          if (match) ncVal = match.nc;
        }
        const cat = ncVal ? activeCatalog.find(c => Number(c.nc || c.id_catalogo_maguey) === Number(ncVal)) : null;
        const tipoStr = m.tipoMaguey || (cat ? `${cat.nombre_cientifico} (${cat.nombre_comun})` : "");

        return {
          id: m.id || Date.now(),
          nc: ncVal ? Number(ncVal) : "",
          idMaguey: ncVal ? Number(ncVal) : "",
          tipoMaguey: tipoStr,
          nombreComun: cat?.nombre_comun || "",
          nombreCientifico: cat?.nombre_cientifico || "",
          edadMadurezAnios: cat?.edad_madurez_anios || null,
          numPinas: m.numPinas ?? m.pinas ?? m.num_pinas ?? "",
          kgMaguey: m.kgMaguey ?? m.kg_maguey ?? "",
          guiaPredio: m.guiaPredio ?? m.guia_predio ?? ""
        };
      });
    }
    return [
      {
        id: Date.now(),
        nc: "",
        idMaguey: "",
        tipoMaguey: "",
        nombreComun: "",
        nombreCientifico: "",
        edadMadurezAnios: null,
        numPinas: "",
        kgMaguey: "",
        guiaPredio: ""
      }
    ];
  }, [formData?.magueyes, formData?.tipoMaguey, formData?.numPinas, formData?.kgMaguey, formData?.guiaPredio, activeCatalog, firstMagueyObj, firstMaguey]);

  // Funciones para agregar, editar y eliminar especies de maguey
  const handleAddMaguey = () => {
    const newMaguey = {
      id: Date.now(),
      nc: "",
      idMaguey: "",
      tipoMaguey: "",
      nombreComun: "",
      nombreCientifico: "",
      edadMadurezAnios: null,
      numPinas: "",
      kgMaguey: "",
      guiaPredio: ""
    };
    setFormData(prev => ({
      ...prev,
      magueyes: [...(prev.magueyes && prev.magueyes.length > 0 ? prev.magueyes : formMagueyes), newMaguey]
    }));
  };

  const handleRemoveMaguey = (id) => {
    const current = Array.isArray(formData?.magueyes) && formData.magueyes.length > 0 ? formData.magueyes : formMagueyes;
    if (current.length <= 1) {
      Swal.fire({
        title: "Atención",
        text: "Debe existir al menos una especie de maguey en el registro de cocción.",
        icon: "info",
        confirmButtonColor: "#4A5D23"
      });
      return;
    }
    setFormData(prev => ({
      ...prev,
      magueyes: current.filter(m => m.id !== id)
    }));
  };

  const handleMagueyChange = (id, field, value) => {
    const current = Array.isArray(formData?.magueyes) && formData.magueyes.length > 0 ? formData.magueyes : formMagueyes;
    setFormData(prev => ({
      ...prev,
      magueyes: current.map(m => m.id === id ? { ...m, [field]: value } : m)
    }));
  };

  // Totales calculados en tiempo real para el formulario
  const formTotalPinas = formMagueyes.reduce((acc, m) => acc + (Number(String(m.numPinas || 0).replace(/[^\d.]/g, '')) || 0), 0);
  const formTotalKgCrudo = formMagueyes.reduce((acc, m) => acc + (Number(String(m.kgMaguey || 0).replace(/[^\d.]/g, '')) || 0), 0);
  const formKgCocido = Number(String(formData?.kgMagueyCocido || 0).replace(/[^\d.]/g, '')) || null;
  const formMermaData = (formTotalKgCrudo && formKgCocido && formTotalKgCrudo > 0) ? {
    kgMerma: Math.max(0, formTotalKgCrudo - formKgCocido),
    pctMerma: (((formTotalKgCrudo - formKgCocido) / formTotalKgCrudo) * 100).toFixed(1)
  } : null;

  const handleEditRecord = (rec) => {
    const isEditable = rec.status === "borrador" || rec.status === "rechazada" || rec.status === "rechazado";
    if (!isEditable) {
      Swal.fire({
        title: "Acción restringida",
        text: "Solo se pueden editar registros que se encuentren en estado BORRADOR o RECHAZADA.",
        icon: "warning",
        confirmButtonColor: "#4A5D23"
      });
      return;
    }

    const parsedMagueyes = (Array.isArray(rec.magueyes) && rec.magueyes.length > 0)
      ? rec.magueyes.map((m, idx) => ({
        id: m.id || Date.now() + idx,
        tipoMaguey: m.tipoMaguey || m.tipo_maguey || firstMaguey,
        numPinas: m.numPinas || m.pinas || m.num_pinas || "",
        kgMaguey: m.kgMaguey || m.kg_maguey ? String(m.kgMaguey || m.kg_maguey).replace(/[^\d.]/g, "") : "",
        guiaPredio: m.guiaPredio || m.guia_predio || rec.guiaPredio || rec.guia_predio || ""
      }))
      : [
        {
          id: Date.now(),
          tipoMaguey: rec.tipoMaguey || firstMaguey,
          numPinas: rec.pinas || rec.numPinas || "",
          kgMaguey: rec.kgMaguey ? String(rec.kgMaguey).replace(/[^\d.]/g, "") : "",
          guiaPredio: rec.guiaPredio || rec.guia_predio || ""
        }
      ];

    setFormData({
      id: rec.id,
      rawId: rec.rawId || (typeof rec.id === 'number' ? rec.id : null),
      idUnidadProduccion: rec.idUnidadProduccion || rec.id_unidad_produccion || "",
      unidadProduccion: rec.unidadProduccion || "",
      noProduccion: rec.noProduccion || "",
      magueyes: parsedMagueyes,
      fechaIngreso: rec.fechaIngreso || "",
      guiaPredio: rec.guiaPredio || "",
      pctArt: rec.pctArt ? String(rec.pctArt).replace(/[^\d.]/g, '') : "",
      noInforme: rec.noInforme || "",
      tipoHorno: rec.tipoHorno || rec.tipo_horno || "",
      fechaInicioCoccion: rec.fechaInicioCoccion || rec.fechaInicio || "",
      fechaFinCoccion: rec.fechaFinCoccion || rec.fechaFin || "",
      kgMagueyCocido: rec.kgMagueyCocido ? String(rec.kgMagueyCocido).replace(/[^\d.]/g, '') : "",
      observaciones: rec.observaciones || "",
      observacionesCorreccion: rec.observacionesCorreccion || rec.observaciones_correccion || "",
      status: rec.status || "borrador"
    });
    setIsFormOpen(true);
  };

  const handleFormSubmit = (status) => {
    const activeMagueyes = Array.isArray(formData?.magueyes) && formData.magueyes.length > 0 ? formData.magueyes : formMagueyes;
    const isBorrador = status === "borrador";
    const missing = [];

    // 1. Identificación del Lote
    const noProd = String(formData?.noProduccion || "").trim();
    if (!noProd) {
      missing.push({
        campo: "No. de Producción (Lote)",
        icono: "🏷️",
        descripcion: "Ingrese el identificador o folio único del lote (ej. PROD-2026-001)."
      });
    }

    // 2. Unidad de Producción / Instalación
    const idUnidad = formData?.idUnidadProduccion || formData?.id_unidad_produccion;
    if (!idUnidad) {
      missing.push({
        campo: "Instalación (Unidad de Producción - Anexo 1)",
        icono: "🏭",
        descripcion: "Seleccione la instalación o palenque autorizado donde se procesará el maguey."
      });
    }

    // 3. Fecha de Ingreso del Maguey
    const fechaIngreso = formData?.fechaIngreso || formData?.fecha_ingreso;
    if (!fechaIngreso) {
      missing.push({
        campo: "Fecha de Ingreso del Maguey",
        icono: "📅",
        descripcion: "Indique la fecha en que ingresó el agave crudo a la instalación."
      });
    }

    // 4. Especies de Maguey & Procedencia
    if (!activeMagueyes || activeMagueyes.length === 0) {
      missing.push({
        campo: "Especies de Maguey",
        icono: "🌱",
        descripcion: "Debe registrar al menos una especie de agave a hornear."
      });
    } else {
      activeMagueyes.forEach((m, idx) => {
        const idxLabel = `Especie #${idx + 1}`;
        const tipoM = String(m.tipoMaguey || "").trim();
        const numP = Number(m.numPinas || 0);
        const kgM = Number(m.kgMaguey || 0);
        const guia = String(m.guiaPredio || m.guia_predio || "").trim();

        if (!tipoM) {
          missing.push({
            campo: `${idxLabel}: Variedad de Maguey`,
            icono: "🌿",
            descripcion: "Seleccione la variedad de agave del catálogo oficial."
          });
        }
        if (numP <= 0 || isNaN(numP)) {
          missing.push({
            campo: `${idxLabel}: Número de Piñas`,
            icono: "🔢",
            descripcion: "Ingrese un número válido de piñas mayor a 0."
          });
        }
        if (kgM <= 0 || isNaN(kgM)) {
          missing.push({
            campo: `${idxLabel}: Kilogramos de Maguey Crudo`,
            icono: "⚖️",
            descripcion: "Ingrese los kilogramos de maguey crudo (> 0 kg)."
          });
        }
        // Guía / Registro de predio es opcional
      });
    }

    // 5. Proceso de Cocción (Obligatorio para enviar a inspección oficial)
    if (!isBorrador) {
      const tipoHorno = String(formData?.tipoHorno || "").trim();
      if (!tipoHorno) {
        missing.push({
          campo: "Tipo de Horno Empleado",
          icono: "🔥",
          descripcion: "Seleccione el tipo de horno utilizado (pozo, mampostería, autoclave)."
        });
      }

      const fechaInicio = formData?.fechaInicioCoccion || formData?.fecha_inicio_coccion;
      if (!fechaInicio) {
        missing.push({
          campo: "Fecha Inicial de Cocción",
          icono: "⏱️",
          descripcion: "Indique la fecha en que inició el encendido y horneado del agave."
        });
      }

      const fechaFin = formData?.fechaFinCoccion || formData?.fecha_fin_coccion;
      if (!fechaFin) {
        missing.push({
          campo: "Fecha Final de Cocción",
          icono: "🏁",
          descripcion: "Indique la fecha en que concluyó el horneado / enfriamiento."
        });
      }

      const kgCocido = Number(String(formData?.kgMagueyCocido || "").replace(/[^\d.]/g, ""));
      if (!kgCocido || kgCocido <= 0 || isNaN(kgCocido)) {
        missing.push({
          campo: "Kg de Maguey Cocido Obtenido",
          icono: "📦",
          descripcion: "Registre el pesaje total de maguey cocido obtenido (> 0 kg)."
        });
      }
    }

    // 6. Inconsistencias de Fechas
    if (formData?.fechaInicioCoccion && formData?.fechaIngreso) {
      if (new Date(formData.fechaInicioCoccion) < new Date(formData.fechaIngreso)) {
        missing.push({
          campo: "Inconsistencia de Fechas: Ingreso vs Inicio Cocción",
          icono: "⚠️",
          descripcion: "La fecha inicial de cocción no puede ser anterior a la fecha de ingreso del maguey."
        });
      }
    }

    if (formData?.fechaInicioCoccion && formData?.fechaFinCoccion) {
      if (new Date(formData.fechaFinCoccion) < new Date(formData.fechaInicioCoccion)) {
        missing.push({
          campo: "Inconsistencia de Fechas: Inicio vs Fin Cocción",
          icono: "⚠️",
          descripcion: "La fecha final de cocción debe ser posterior o igual a la fecha inicial."
        });
      }
    }

    // 7. % ART
    if (formData?.pctArt !== undefined && formData?.pctArt !== null && String(formData.pctArt).trim() !== "") {
      const numArt = Number(String(formData.pctArt).replace(/[^\d.]/g, ""));
      if (isNaN(numArt) || numArt < 0 || numArt > 100) {
        missing.push({
          campo: "Porcentaje de % ART Inválido",
          icono: "🧪",
          descripcion: "El % de Azúcares Reductores Totales debe ser un número entre 0% y 100%."
        });
      }
    }

    // Si faltan campos obligatorios, mostrar modal detallado
    if (missing.length > 0) {
      Swal.fire({
        title: '<div style="display:flex;align-items:center;justify-content:center;gap:10px;color:#991B1B;font-size:20px;font-weight:800;"><span style="font-size:24px;">⚠️</span> Campos Obligatorios Incompletos</div>',
        html: `
          <div style="text-align:left;font-family:inherit;">
            <p style="margin:0 0 12px 0;color:#4B5563;font-size:13px;line-height:1.5;">
              Para ${isBorrador ? 'guardar el registro' : 'enviar la <strong>Bitácora de Cocción</strong> a revisión oficial'}, es indispensable completar los siguientes <strong>${missing.length} dato(s) obligatorio(s)</strong>:
            </p>
            <div style="max-height:280px;overflow-y:auto;background:#FFF5F5;border:1px solid #FECACA;border-radius:12px;padding:12px 14px;">
              <ul style="list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:10px;">
                ${missing.map((item, idx) => `
                  <li style="display:flex;align-items:flex-start;gap:10px;padding-bottom:8px;border-bottom:${idx === missing.length - 1 ? 'none' : '1px solid #FEE2E2'};">
                    <span style="font-size:16px;line-height:1;margin-top:2px;">${item.icono || '❌'}</span>
                    <div style="flex:1;">
                      <strong style="color:#991B1B;font-size:13px;display:block;">${item.campo}</strong>
                      <span style="color:#6B7280;font-size:11.5px;line-height:1.4;display:block;margin-top:2px;">${item.descripcion}</span>
                    </div>
                  </li>
                `).join('')}
              </ul>
            </div>
            <div style="margin-top:14px;padding:10px 14px;background:#F0FDF4;border:1px solid #BBF7D0;border-radius:10px;display:flex;align-items:center;gap:10px;">
              <span style="font-size:18px;">💡</span>
              <span style="font-size:11.5px;color:#166534;line-height:1.4;">
                Por favor complete los campos obligatorios indicados con asterisco (*) en el formulario para proceder.
              </span>
            </div>
          </div>
        `,
        confirmButtonText: 'Entendido, Completar Formulario',
        confirmButtonColor: '#4A5D23',
        background: '#FFFFFF',
        width: '580px',
        customClass: {
          popup: 'swal2-custom-border-radius'
        }
      });
      return;
    }

    const sanitizedMagueyes = activeMagueyes.map(m => {
      const rawGuia = (m.guiaPredio !== undefined && m.guiaPredio !== null)
        ? String(m.guiaPredio).trim()
        : ((m.guia_predio !== undefined && m.guia_predio !== null) ? String(m.guia_predio).trim() : "");
      // En caso de que no agregue un número de guía, se asigna internamente un espacio (" ") para que nunca quede null ni undefined
      const finalGuia = rawGuia !== "" ? rawGuia : " ";
      return {
        ...m,
        guiaPredio: finalGuia,
        guia_predio: finalGuia
      };
    });

    const totalPinas = sanitizedMagueyes.reduce((acc, m) => acc + (Number(String(m.numPinas || 0).replace(/[^\d.]/g, "")) || 0), 0);
    const totalKg = sanitizedMagueyes.reduce((acc, m) => acc + (Number(String(m.kgMaguey || 0).replace(/[^\d.]/g, "")) || 0), 0);
    const summaryTipo = sanitizedMagueyes.map(m => m.tipoMaguey).filter(Boolean).join(" + ") || firstMaguey;
    const guiasConTexto = sanitizedMagueyes.map(m => m.guiaPredio.trim()).filter(Boolean);
    const summaryGuia = guiasConTexto.length > 0 
      ? guiasConTexto.join(" / ") 
      : ((formData.guiaPredio && formData.guiaPredio.trim()) || (formData.guia_predio && formData.guia_predio.trim()) || " ");

    const dataToSave = {
      ...formData,
      magueyes: sanitizedMagueyes,
      numPinas: totalPinas,
      pinas: totalPinas,
      kgMaguey: totalKg,
      tipoMaguey: summaryTipo,
      guiaPredio: summaryGuia,
      guia_predio: summaryGuia
    };

    if (handleSaveCoccion) {
      handleSaveCoccion(status, dataToSave);
    }
  };

  return (
    <div className="animate-fadeIn">
      {/* ENCABEZADO Y FILTROS */}
      <div className="history-section-header" style={{ flexWrap: "wrap", gap: "16px" }}>
        <div>
          <h2>Historial de Bitácoras de Cocción (Sección II)</h2>
          <p className="subtitle-history">Registros oficiales de horneado e ingreso de materia prima (agaves y ensambles)</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          {/* Filtro por estatus */}
          <select
            value={statusFilter}
            onChange={(e) => handleStatusFilterChange(e.target.value)}
            style={{
              padding: "9px 14px",
              borderRadius: "12px",
              border: "1px solid #D1D5DB",
              fontSize: "13px",
              fontWeight: 600,
              color: "#374151",
              backgroundColor: "white",
              outline: "none",
              cursor: "pointer"
            }}
          >
            <option value="todos">Todos los Estatus</option>
            <option value="borrador">Borradores</option>
            <option value="enviada">Enviadas</option>
            <option value="aceptada">Aceptadas</option>
            <option value="rechazada">Rechazadas</option>
            <option value="cancelada">Canceladas</option>
          </select>

          {/* Campo de búsqueda */}
          <div style={{ position: "relative", minWidth: "240px" }}>
            <SearchIcon style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", fontSize: 20 }} />
            <input
              type="text"
              placeholder="Buscar por no. prod, predio, maguey..."
              value={coccionSearch}
              onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
              style={{
                width: "100%",
                paddingLeft: "38px",
                paddingRight: coccionSearch ? "36px" : "12px",
                paddingTop: "9px",
                paddingBottom: "9px",
                borderRadius: "12px",
                border: "1px solid #D1D5DB",
                fontSize: "13px",
                outline: "none",
                backgroundColor: "white"
              }}
            />
            {coccionSearch && (
              <button
                type="button"
                onClick={() => onSearchChange && onSearchChange("")}
                style={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: "#9CA3AF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "4px"
                }}
                title="Limpiar búsqueda"
              >
                <CloseIcon style={{ fontSize: 16 }} />
              </button>
            )}
          </div>
          <button
            onClick={() => {
              setFormData({
                id: null,
                rawId: null,
                idUnidadProduccion: "",
                unidadProduccion: "",
                noProduccion: "",
                magueyes: [
                  {
                    id: Date.now(),
                    nc: "",
                    tipoMaguey: "",
                    numPinas: "",
                    kgMaguey: "",
                    guiaPredio: ""
                  }
                ],
                fechaIngreso: "",
                guiaPredio: "",
                pctArt: "",
                noInforme: "",
                tipoHorno: "",
                fechaInicioCoccion: "",
                fechaFinCoccion: "",
                kgMagueyCocido: "",
                observaciones: "",
                status: "borrador"
              });
              setIsFormOpen(true);
            }}
            className="btn-add-new"
          >
            <AddIcon />
            <span>Nueva Registro de Cocción</span>
          </button>
        </div>
      </div>

      {/* TABLA HISTÓRICA */}
      {filteredRecords.length === 0 ? (
        <div className="empty-state-container" style={{ background: "white", borderRadius: "16px", border: "1px solid var(--glass-border)", padding: "48px 24px", margin: "16px 0", textAlign: "center" }}>
          <InboxIcon style={{ fontSize: 48, color: "var(--copper)", opacity: 0.6, marginBottom: "12px" }} />
          <h3 style={{ margin: "0 0 6px 0", color: "var(--text-primary)", fontSize: "16px", fontWeight: 700 }}>
            {coccionSearch || statusFilter !== "todos" ? "No se encontraron resultados" : "No hay bitácoras de cocción registradas"}
          </h3>
          <p style={{ margin: 0, color: "var(--text-muted)", fontSize: "13px" }}>
            {coccionSearch || statusFilter !== "todos" ? `No se hallaron registros coincidentes con los filtros aplicados.` : "Actualmente no existen registros guardados. Haga clic en 'Nueva Registro de Cocción' para agregar uno."}
          </p>
        </div>
      ) : (
        <div className="table-responsive-container">
          <table className="custom-table-bitacora">
            <thead>
              <tr>
                <th>No. Producción</th>
                <th>Unidad de Producción</th>
                <th>Especies de Maguey</th>
                <th>Total kg Crudo</th>
                <th>Fecha Ingreso</th>
                <th>Inspector</th>
                <th>Estatus</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.map((rec) => {
                const recMagueyes = Array.isArray(rec.magueyes) && rec.magueyes.length > 0 ? rec.magueyes : [];
                const rawKg = recMagueyes.length > 0
                  ? recMagueyes.reduce((acc, m) => acc + (Number(String(m.kgMaguey || m.kg_maguey || 0).replace(/[^\d.]/g, '')) || 0), 0)
                  : (rec.kgMaguey != null ? Number(String(rec.kgMaguey).replace(/[^\d.]/g, '')) : null);

                const kgMagueyStr = rawKg ? `${rawKg.toLocaleString()} kg` : (rec.kgMaguey || '-');
                const { totalKgCocidos, kgUsados, kgDisponibles, isAgotada, pctConsumido, moliendasCount } = getCoccionMoliendaBalance(rec, moliendaRecords);
                const normStatus = String(rec.status || rec.statusUI || '').toLowerCase();
                const isAceptada = normStatus.includes('aceptad') || normStatus.includes('aprobad');
                const isCancelada = normStatus.includes('cancelad');

                return (
                  <tr key={rec.id}>
                    <td className="font-bold-primary">{rec.noProduccion}</td>
                    <td>
                      {(() => {
                        const targetId = rec.idUnidadProduccion || rec.id_unidad_produccion;
                        const inst = instalaciones.find(i => String(i.idanexocliente) === String(targetId));
                        const alias = rec.alias_instalacion || inst?.informaciongeneralinstalacioncliente?.alias || inst?.alias || (targetId ? `Unidad #${targetId}` : '-');
                        return <span style={{ fontWeight: 600, color: "var(--text-primary)", fontSize: "12.5px" }}>{alias}</span>;
                      })()}
                    </td>
                    <td>
                      {recMagueyes.length > 1 ? (
                        <span className="chip-maguey" style={{ background: "rgba(184, 115, 51, 0.1)", color: "var(--copper)", borderColor: "rgba(184, 115, 51, 0.25)" }}>
                          Ensamble ({recMagueyes.length} Especies)
                        </span>
                      ) : (
                        <span className="chip-maguey">
                          {rec.tipoMaguey || (recMagueyes[0]?.tipoMaguey) || 'Espadín'}
                        </span>
                      )}
                    </td>
                    <td style={{ fontWeight: 700 }}>
                      <div style={{ color: "var(--agave-green)", fontWeight: 800 }}>{kgMagueyStr}</div>
                      {!isCancelada && (
                        <div style={{ marginTop: "3px" }}>
                          {isAgotada ? (
                            <span className="chip-agotado" title="Materia prima 100% procesada en molienda">
                              100% en Molienda
                            </span>
                          ) : (
                            <span className="chip-disponible" style={{ fontSize: "10.5px", padding: "2px 7px" }} title={`${kgDisponibles.toLocaleString()} kg disponibles de ${totalKgCocidos.toLocaleString()} kg`}>
                              {kgDisponibles.toLocaleString()} kg disp.
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td style={{ color: "var(--text-secondary)", fontSize: "12.5px" }}>{rec.fechaIngreso || '-'}</td>
                    <td>
                      <span className="inspector-tag">{rec.inspector || 'Pendiente de V.Bo.'}</span>
                    </td>
                    <td>
                      {renderCoccionStatusBadge(rec.status)}
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: "6px", justifyContent: "center", alignItems: "center" }}>
                        <button
                          onClick={() => setSelectedRecord(rec)}
                          title="Ver detalles completos"
                          className="btn-action-icon view"
                        >
                          <VisibilityIcon style={{ fontSize: 16 }} />
                        </button>
                        {!isCancelada && !isAgotada && onOpenNewMolienda && (
                          <button
                            onClick={() => onOpenNewMolienda(rec)}
                            title={`Iniciar Molienda (${kgDisponibles.toLocaleString()} kg disponibles)`}
                            className="btn-action-icon molienda"
                          >
                            <ScienceIcon style={{ fontSize: 16 }} />
                          </button>
                        )}
                        {(rec.status === "borrador" || rec.status === "rechazada" || rec.status === "rechazado") && (
                          <button
                            onClick={() => handleEditRecord(rec)}
                            title={rec.status === "rechazada" || rec.status === "rechazado" ? "Editar y corregir bitácora" : "Editar Borrador"}
                            className="btn-action-icon edit"
                          >
                            <EditIcon style={{ fontSize: 16 }} />
                          </button>
                        )}
                        {rec.status !== "cancelada" && rec.status !== "cancelado" && rec.status !== "aprobado" && rec.status !== "aceptada" && rec.status !== "aceptado" && handleCancelCoccion && (
                          <button
                            onClick={() => handleCancelCoccion(rec)}
                            title="Cancelar Bitácora"
                            className="btn-action-icon cancel"
                          >
                            <CancelIcon style={{ fontSize: 16 }} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* CONTROLES DE PAGINACIÓN */}
      {(() => {
        const totalRecs = pagination?.totalRecords ?? filteredRecords.length;
        const totalPages = pagination?.totalPages ?? Math.ceil(totalRecs / (pagination?.limit || 10)) ?? 1;
        const currentPage = pagination?.currentPage || 1;

        if (totalRecs <= 0 && filteredRecords.length === 0) return null;

        return (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "16px",
              padding: "12px 18px",
              background: "white",
              borderRadius: "14px",
              border: "1px solid var(--glass-border)",
              boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
              flexWrap: "wrap",
              gap: "12px"
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ fontSize: "13px", color: "var(--text-secondary)", fontWeight: 500 }}>
                Mostrando página <strong>{currentPage}</strong> de <strong>{totalPages || 1}</strong> ({totalRecs} registros en total)
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <button
                type="button"
                disabled={currentPage <= 1}
                onClick={() => onPageChange && onPageChange(currentPage - 1)}
                className="btn-secondary-custom"
                style={{
                  padding: "6px 14px",
                  fontSize: "12px",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  cursor: currentPage <= 1 ? "not-allowed" : "pointer",
                  opacity: currentPage <= 1 ? 0.45 : 1,
                  borderRadius: "8px",
                  border: "1px solid #D1D5DB",
                  backgroundColor: "white"
                }}
              >
                <NavigateBeforeIcon style={{ fontSize: 18 }} /> Anterior
              </button>

              <span
                style={{
                  fontSize: "12.5px",
                  fontWeight: 700,
                  padding: "4px 10px",
                  background: "#F3F4F6",
                  borderRadius: "8px",
                  color: "#374151"
                }}
              >
                {currentPage} / {totalPages || 1}
              </span>

              <button
                type="button"
                disabled={currentPage >= totalPages}
                onClick={() => onPageChange && onPageChange(currentPage + 1)}
                className="btn-secondary-custom"
                style={{
                  padding: "6px 14px",
                  fontSize: "12px",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  cursor: currentPage >= totalPages ? "not-allowed" : "pointer",
                  opacity: currentPage >= totalPages ? 0.45 : 1,
                  borderRadius: "8px",
                  border: "1px solid #D1D5DB",
                  backgroundColor: "white"
                }}
              >
                Siguiente <NavigateNextIcon style={{ fontSize: 18 }} />
              </button>
            </div>
          </div>
        );
      })()}


      {/* =========================================================================
          MODAL 1: DETALLE COMPLETO DE COCCIÓN (CON N TIPOS DE MAGUEY)
      ========================================================================= */}
      {selectedRecord && createPortal(
        <div
          className="detail-modal-overlay"
          onClick={() => setSelectedRecord(null)}
        >
          <div
            className="detail-modal-card animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "920px" }}
          >
            {/* Header del Modal */}
            <div className="detail-modal-header header-theme-coccion">
              <div className="modal-header-title">
                <div className="icon-badge">
                  <LocalFireDepartmentIcon />
                </div>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                    <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 800 }}>
                      Detalle Bitácora de Cocción: {selectedRecord.noProduccion || 'Sin Folio'}
                    </h3>
                    {renderModalHeaderStatusBadge(selectedRecord.status)}
                  </div>
                  <p style={{ margin: "3px 0 0 0", fontSize: "12px", opacity: 0.95 }}>
                    Sección II: Registro de horneado, piñas e ingreso de especies de agave
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setSelectedRecord(null)}
                className="modal-close-btn"
                title="Cerrar modal"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Cuerpo del Modal con Scroll */}
            <div className="detail-modal-body modal-body-scrollable" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {/* Banner de Rechazo / Corrección */}
              {(selectedRecord.status === "rechazada" || selectedRecord.status === "rechazado") && (
                <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: "14px", padding: "14px 18px", display: "flex", gap: "12px", alignItems: "flex-start" }}>
                  <ReportProblemIcon style={{ color: "#DC2626", marginTop: "2px" }} />
                  <div>
                    <h4 style={{ margin: "0 0 4px 0", color: "#991B1B", fontSize: "13.5px", fontWeight: 700 }}>
                      Bitácora Rechazada por el Área de Inspección
                    </h4>
                    <p style={{ margin: 0, color: "#7F1D1D", fontSize: "12.5px" }}>
                      <strong>Observaciones del inspector:</strong> {selectedRecord.observacionesCorreccion || selectedRecord.observaciones_correccion || "Se requiere revisión y corrección de la información ingresada."}
                    </p>
                  </div>
                </div>
              )}

              {(() => {
                const selectedInst = effectiveInstalaciones.find(i => String(i.idanexocliente || i.id) === String(selectedRecord.idUnidadProduccion || selectedRecord.id_unidad_produccion));
                const aliasInst = selectedRecord.alias_instalacion || selectedRecord.unidadProduccion || selectedInst?.informaciongeneralinstalacioncliente?.alias || selectedInst?.alias || (selectedRecord.idUnidadProduccion ? `Unidad de Producción #${selectedRecord.idUnidadProduccion}` : "No especificada");

                let dirInst = selectedRecord.domicilio_instalacion || selectedInst?.informaciongeneralinstalacioncliente?.direccion;
                if (!dirInst && selectedInst?.informaciongeneralinstalacioncliente) {
                  const info = selectedInst.informaciongeneralinstalacioncliente;
                  dirInst = [info.calle, info.numeroexterior || info.numext, info.colonia, info.municipio, info.estado].filter(Boolean).join(", ");
                }
                dirInst = dirInst || selectedInst?.direccion || "";

                // Lista de magueyes del registro seleccionado
                const detailMagueyes = (Array.isArray(selectedRecord.magueyes) && selectedRecord.magueyes.length > 0)
                  ? selectedRecord.magueyes
                  : [
                    {
                      id: 1,
                      tipoMaguey: selectedRecord.tipoMaguey || selectedRecord.tipo_maguey || "Agave angustifolia Haw (Espadín)",
                      numPinas: selectedRecord.pinas || selectedRecord.numPinas || selectedRecord.num_pinas || 0,
                      kgMaguey: selectedRecord.kgMaguey || selectedRecord.kg_maguey || 0
                    }
                  ];

                const totalPinas = detailMagueyes.reduce((acc, m) => acc + (Number(String(m.numPinas || m.pinas || 0).replace(/[^\d.]/g, '')) || 0), 0) || (Number(selectedRecord.pinas || selectedRecord.numPinas || 0));
                const rawKgCrudo = detailMagueyes.reduce((acc, m) => acc + (Number(String(m.kgMaguey || m.kg_maguey || 0).replace(/[^\d.]/g, '')) || 0), 0) || (Number(String(selectedRecord.kgMaguey != null ? selectedRecord.kgMaguey : (selectedRecord.kg_maguey || 0)).replace(/[^\d.]/g, '')) || 0);
                const rawKgCocido = Number(String(selectedRecord.kgMagueyCocido != null ? selectedRecord.kgMagueyCocido : (selectedRecord.kg_maguey_cocido || 0)).replace(/[^\d.]/g, '')) || null;

                const mermaData = (rawKgCrudo && rawKgCocido && rawKgCrudo > 0) ? {
                  kgMerma: Math.max(0, rawKgCrudo - rawKgCocido),
                  pctMerma: (((rawKgCrudo - rawKgCocido) / rawKgCrudo) * 100).toFixed(1)
                } : null;

                return (
                  <>
                    {/* Resumen Métrico de Totales */}
                    <section
                      className="detail-card"
                      style={{
                        margin: 0,
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
                        gap: "12px",
                        padding: "14px 18px",
                        alignItems: "center",
                        background: "#FFFFFF",
                        border: "1px solid #E2E8F0",
                        borderRadius: "14px"
                      }}
                    >
                      <div style={{ textAlign: "center" }}>
                        <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 800, letterSpacing: "0.4px" }}>Especies</span>
                        <h4 style={{ margin: "3px 0 0 0", color: "var(--text-primary)", fontSize: "18px", fontWeight: 800 }}>
                          {detailMagueyes.length} <small style={{ fontSize: "12px", color: "var(--text-muted)" }}>{detailMagueyes.length === 1 ? 'especie' : 'especies'}</small>
                        </h4>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 800, letterSpacing: "0.4px" }}>Total Piñas</span>
                        <h4 style={{ margin: "3px 0 0 0", color: "var(--agave-green)", fontSize: "18px", fontWeight: 800 }}>
                          {totalPinas.toLocaleString()} <small style={{ fontSize: "12px", color: "var(--text-muted)" }}>pzas</small>
                        </h4>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 800, letterSpacing: "0.4px" }}>Total Crudo</span>
                        <h4 style={{ margin: "3px 0 0 0", color: "var(--agave-green)", fontSize: "18px", fontWeight: 800 }}>
                          {rawKgCrudo.toLocaleString()} <small style={{ fontSize: "12px", color: "var(--text-muted)" }}>kg</small>
                        </h4>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 800, letterSpacing: "0.4px" }}>Total Cocido</span>
                        <h4 style={{ margin: "3px 0 0 0", color: "var(--copper)", fontSize: "18px", fontWeight: 800 }}>
                          {rawKgCocido ? `${rawKgCocido.toLocaleString()} ` : '- '}
                          <small style={{ fontSize: "12px", color: "var(--text-muted)" }}>kg</small>
                        </h4>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 800, letterSpacing: "0.4px" }}>Merma</span>
                        <h4 style={{ margin: "3px 0 0 0", color: mermaData ? "#D97706" : "var(--text-muted)", fontSize: "17px", fontWeight: 800 }}>
                          {mermaData ? `${mermaData.pctMerma}%` : '-'}
                        </h4>
                      </div>
                    </section>

                    {/* Tarjeta 1: Materia Prima e Instalación */}
                    <section className="detail-card detail-card-accent-left-green" style={{ margin: 0 }}>
                      <header className="detail-card-header">
                        <div className="detail-card-icon-wrapper-green">
                          <GrassIcon />
                        </div>
                        <div className="detail-card-title-wrapper">
                          <h2>Materia Prima & Datos de Cosecha</h2>
                          <p>Instalación autorizada, origen y desglose de especies de maguey</p>
                        </div>
                      </header>

                      {/* Instalación / Unidad de Producción */}
                      <div className="integrated-input-box" style={{ marginBottom: "14px" }}>
                        <span className="integrated-input-label">
                          INSTALACIÓN (UNIDAD DE PRODUCCIÓN - ANEXO 1)
                        </span>
                        <div className="integrated-display-value" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "2px" }}>
                          <strong style={{ fontSize: "13.5px", color: "var(--text-primary)" }}>{aliasInst}</strong>
                          {dirInst && (
                            <span style={{ fontSize: "11.5px", color: "var(--text-muted)", fontWeight: 500 }}>
                              {dirInst}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* No. de Producción & Guía/Predio */}
                      <div className="species-grid" style={{ marginBottom: "14px" }}>
                        <div className="integrated-input-box">
                          <span className="integrated-input-label">NO. DE PRODUCCIÓN</span>
                          <div className="integrated-display-value">
                            <strong style={{ fontSize: "14px", color: "var(--agave-green)", fontWeight: 800 }}>
                              {selectedRecord.noProduccion || "-"}
                            </strong>
                          </div>
                        </div>

                        <div className="integrated-input-box">
                          <span className="integrated-input-label">FECHA INGRESO MAGUEY</span>
                          <div className="integrated-display-value">
                            {selectedRecord.fechaIngreso || selectedRecord.fecha_ingreso || "-"}
                          </div>
                        </div>
                      </div>

                      {/* DESGLOSE DE ESPECIES DE MAGUEY INGRESADAS (N ESPECIES) */}
                      <div style={{ marginTop: "16px", marginBottom: "14px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                          <span style={{ fontSize: "12px", fontWeight: 800, color: "var(--text-primary)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                            Especies de Maguey Ingresadas ({detailMagueyes.length})
                          </span>
                        </div>

                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                          {detailMagueyes.map((m, idx) => {
                            const mKg = Number(String(m.kgMaguey || m.kg_maguey || 0).replace(/[^\d.]/g, '')) || 0;
                            const mPinas = Number(String(m.numPinas || m.pinas || 0).replace(/[^\d.]/g, '')) || 0;
                            const pctParticipation = rawKgCrudo > 0 && mKg > 0 ? ((mKg / rawKgCrudo) * 100).toFixed(1) : null;

                            return (
                              <div
                                key={m.id || idx}
                                style={{
                                  background: "#F8FAFC",
                                  border: "1px solid #E2E8F0",
                                  borderRadius: "12px",
                                  padding: "12px 16px",
                                  display: "grid",
                                  gridTemplateColumns: "1.35fr 0.85fr 0.85fr 0.95fr",
                                  gap: "12px",
                                  alignItems: "center"
                                }}
                              >
                                <div>
                                  <span style={{ fontSize: "10.5px", color: "var(--text-muted)", fontWeight: 700, textTransform: "uppercase" }}>Especie #{idx + 1}</span>
                                  <div style={{ marginTop: "2px" }}>
                                    <span className="chip-maguey" style={{ fontSize: "12px" }}>
                                      {m.tipoMaguey || m.tipo_maguey || "Agave angustifolia Haw (Espadín)"}
                                    </span>
                                  </div>
                                </div>

                                <div>
                                  <span style={{ fontSize: "10.5px", color: "var(--text-muted)", fontWeight: 700, textTransform: "uppercase" }}>Número de Piñas</span>
                                  <div style={{ fontSize: "13.5px", fontWeight: 800, color: "var(--text-primary)", marginTop: "2px" }}>
                                    {mPinas.toLocaleString()} <span style={{ fontSize: "11px", color: "var(--text-muted)", fontWeight: 500 }}>piñas</span>
                                  </div>
                                </div>

                                <div>
                                  <span style={{ fontSize: "10.5px", color: "var(--text-muted)", fontWeight: 700, textTransform: "uppercase" }}>Kg Maguey Crudo</span>
                                  <div style={{ fontSize: "13.5px", fontWeight: 800, color: "var(--agave-green)", marginTop: "2px" }}>
                                    {mKg.toLocaleString()} <span style={{ fontSize: "11px", color: "var(--text-muted)", fontWeight: 500 }}>kg</span>
                                    {pctParticipation && (
                                      <span style={{ fontSize: "11px", color: "var(--copper)", marginLeft: "6px", fontWeight: 700 }}>
                                        ({pctParticipation}%)
                                      </span>
                                    )}
                                  </div>
                                </div>

                                <div>
                                  <span style={{ fontSize: "10.5px", color: "var(--text-muted)", fontWeight: 700, textTransform: "uppercase" }}>N° de Guía / Predio</span>
                                  <div style={{ fontSize: "13px", fontWeight: 700, color: "var(--text-primary)", marginTop: "2px" }}>
                                    {m.guiaPredio?.trim() || m.guia_predio?.trim() || selectedRecord.guiaPredio?.trim() || selectedRecord.guia_predio?.trim() || "-"}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* % de ART en Maguey Crudo & N° de Informe */}
                      <div className="species-grid">
                        <div className="integrated-input-box">
                          <span className="integrated-input-label">% DE ART EN MAGUEY CRUDO</span>
                          <div className="integrated-display-value">
                            {selectedRecord.pctArt != null && selectedRecord.pctArt !== "" && selectedRecord.pctArt !== "-"
                              ? (String(selectedRecord.pctArt).includes('%') ? selectedRecord.pctArt : `${selectedRecord.pctArt}%`)
                              : "---"}
                          </div>
                          <span className="integrated-input-suffix">% ART</span>
                        </div>

                        <div className="integrated-input-box">
                          <span className="integrated-input-label">N° DE INFORME DE ENSAYO</span>
                          <div className="integrated-display-value">
                            {selectedRecord.noInforme || selectedRecord.no_informe || "---"}
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* Tarjeta 2: Proceso de Cocción */}
                    <section className="detail-card detail-card-accent-left-copper" style={{ margin: 0 }}>
                      <header className="detail-card-header">
                        <div className="detail-card-icon-wrapper-copper">
                          <LocalFireDepartmentIcon />
                        </div>
                        <div className="detail-card-title-wrapper">
                          <h2>Proceso de Cocción</h2>
                          <p>Horneado del agave y control de peso cocido</p>
                        </div>
                      </header>

                      {/* Tipo de Horno */}
                      <div className="integrated-input-box" style={{ marginBottom: "14px" }}>
                        <span className="integrated-input-label">TIPO DE HORNO</span>
                        <div className="integrated-display-value">
                          {selectedRecord.tipoHorno || selectedRecord.tipo_horno || "Horno de pozo"}
                        </div>
                      </div>

                      {/* Fecha Inicial & Fecha Final de Cocción */}
                      <div className="species-grid" style={{ marginBottom: "14px" }}>
                        <div className="integrated-input-box">
                          <span className="integrated-input-label">FECHA INICIAL DE COCCIÓN</span>
                          <div className="integrated-display-value">
                            {selectedRecord.fechaInicioCoccion || selectedRecord.fecha_inicio_coccion || selectedRecord.fechaInicio || "-"}
                          </div>
                        </div>

                        <div className="integrated-input-box">
                          <span className="integrated-input-label">FECHA FINAL DE COCCIÓN</span>
                          <div className="integrated-display-value">
                            {selectedRecord.fechaFinCoccion || selectedRecord.fecha_fin_coccion || selectedRecord.fechaFin || "-"}
                          </div>
                        </div>
                      </div>

                      {/* Kg de Maguey Cocido & Merma Estimada */}
                      <div className="species-grid" style={{ marginBottom: "14px" }}>
                        <div className="integrated-input-box">
                          <span className="integrated-input-label">KG DE MAGUEY COCIDO</span>
                          <div className="integrated-display-value">
                            <strong style={{ color: "var(--copper)", fontWeight: 800 }}>
                              {rawKgCocido ? `${rawKgCocido.toLocaleString()} kg` : (selectedRecord.kgMagueyCocido || selectedRecord.kg_maguey_cocido || "-")}
                            </strong>
                          </div>
                          <span className="integrated-input-suffix">kg</span>
                        </div>

                        <div className="integrated-input-box">
                          <span className="integrated-input-label">MERMA DE COCCIÓN ESTIMADA</span>
                          <div className="integrated-display-value">
                            {mermaData != null ? (
                              <span style={{ fontWeight: 700, color: mermaData.kgMerma > 0 ? "#D97706" : "var(--text-primary)" }}>
                                {mermaData.kgMerma.toLocaleString()} kg ({mermaData.pctMerma}%)
                              </span>
                            ) : (
                              <span style={{ color: "var(--text-muted)" }}>-</span>
                            )}
                          </div>
                          <span className="integrated-input-suffix">% merma</span>
                        </div>
                      </div>

                      {/* Observaciones */}
                      <div className="integrated-input-box">
                        <span className="integrated-input-label">OBSERVACIONES</span>
                        <div className="integrated-display-value" style={{ minHeight: "36px", whiteSpace: "pre-wrap", fontWeight: 500, fontSize: "13px", marginTop: "2px" }}>
                          {selectedRecord.observaciones || "Sin observaciones adicionales registradas."}
                        </div>
                      </div>
                    </section>

                    {/* Tarjeta: Seguimiento de Molienda y Saldo Disponible */}
                    {(() => {
                      const { totalKgCocidos, kgUsados, kgDisponibles, isAgotada, pctConsumido, moliendasCount } = getCoccionMoliendaBalance(selectedRecord, moliendaRecords);
                      const normSt = String(selectedRecord.status || selectedRecord.statusUI || '').toLowerCase();
                      const isCancelada = normSt.includes('cancelad');
                      if (isCancelada) return null;

                      return (
                        <section className="detail-card detail-card-accent-left-copper" style={{ margin: 0 }}>
                          <header className="detail-card-header">
                            <div className="detail-card-icon-wrapper-copper">
                              <ScienceIcon />
                            </div>
                            <div className="detail-card-title-wrapper">
                              <h2>Seguimiento a Molienda & Fermentación</h2>
                              <p>Control de materia prima procesada en tinas y saldo disponible</p>
                            </div>
                          </header>

                          <div className="balance-kpi-grid" style={{ marginTop: "10px" }}>
                            <div className="balance-kpi-card">
                              <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 800 }}>Maguey Cocido Total</span>
                              <h4 style={{ margin: "4px 0 0 0", color: "var(--agave-green)", fontSize: "17px", fontWeight: 800 }}>
                                {totalKgCocidos.toLocaleString()} <small style={{ fontSize: "12px", color: "var(--text-muted)" }}>kg</small>
                              </h4>
                            </div>

                            <div className="balance-kpi-card">
                              <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 800 }}>Procesado ({moliendasCount} {moliendasCount === 1 ? 'Molienda' : 'Moliendas'})</span>
                              <h4 style={{ margin: "4px 0 0 0", color: "var(--copper)", fontSize: "17px", fontWeight: 800 }}>
                                {kgUsados.toLocaleString()} <small style={{ fontSize: "12px", color: "var(--text-muted)" }}>kg</small>
                              </h4>
                            </div>

                            <div className={`balance-kpi-card ${!isAgotada ? 'highlight-available' : ''}`}>
                              <span style={{ fontSize: "11px", color: !isAgotada ? "#047857" : "var(--text-muted)", textTransform: "uppercase", fontWeight: 800 }}>Saldo Disponible</span>
                              <h4 style={{ margin: "4px 0 0 0", color: !isAgotada ? "#047857" : "#64748B", fontSize: "17px", fontWeight: 800 }}>
                                {kgDisponibles.toLocaleString()} <small style={{ fontSize: "12px", color: !isAgotada ? "#047857" : "var(--text-muted)" }}>kg</small>
                              </h4>
                            </div>
                          </div>

                          <div style={{ marginTop: "12px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "var(--text-muted)", fontWeight: 700 }}>
                              <span>Progreso de Molienda</span>
                              <span>{pctConsumido}% ({isAgotada ? 'Materia Prima 100% Agotada' : `${kgDisponibles.toLocaleString()} kg disponibles`})</span>
                            </div>
                            <div className="balance-progress-track">
                              <div className={`balance-progress-fill ${isAgotada ? 'full' : ''}`} style={{ width: `${pctConsumido}%` }} />
                            </div>
                          </div>
                        </section>
                      );
                    })()}

                    {/* Tarjeta 3: Dictamen e Inspección UI */}
                    <section className="detail-card" style={{ background: "#FAF9F6", border: "1px solid #EAE6DF", borderRadius: "16px", padding: "18px", margin: 0 }}>
                      <header className="detail-card-header" style={{ marginBottom: "12px" }}>
                        <div className="detail-card-icon-wrapper-copper" style={{ background: "rgba(184, 115, 51, 0.12)", color: "var(--copper)" }}>
                          <FactCheckIcon />
                        </div>
                        <div className="detail-card-title-wrapper">
                          <h2 style={{ fontSize: "14px", fontWeight: 800 }}>Dictamen e Inspección UI</h2>
                          <p style={{ fontSize: "11.5px" }}>Estado de validación oficial y asignación del inspector</p>
                        </div>
                      </header>

                      <div className="species-grid">
                        <div className="integrated-input-box">
                          <span className="integrated-input-label">INSPECTOR ASIGNADO</span>
                          <div className="integrated-display-value">
                            <span className="inspector-tag">
                              {selectedRecord.inspector || 'Pendiente de V.Bo.'}
                            </span>
                          </div>
                        </div>

                        <div className="integrated-input-box">
                          <span className="integrated-input-label">ESTATUS DE LA BITÁCORA</span>
                          <div className="integrated-display-value">
                            {renderCoccionStatusBadge(selectedRecord.status)}
                          </div>
                        </div>
                      </div>
                    </section>
                  </>
                );
              })()}
            </div>

            {/* Footer del Modal */}
            <div className="detail-modal-footer">
              {(() => {
                const { kgDisponibles, isAgotada } = getCoccionMoliendaBalance(selectedRecord, moliendaRecords);
                const normSt = String(selectedRecord?.status || selectedRecord?.statusUI || '').toLowerCase();
                const isCancelada = normSt.includes('cancelad');
                if (isCancelada || isAgotada || !onOpenNewMolienda) return null;

                return (
                  <button
                    type="button"
                    onClick={() => {
                      const rec = selectedRecord;
                      setSelectedRecord(null);
                      onOpenNewMolienda(rec);
                    }}
                    className="btn-add-new header-theme-molienda"
                    style={{ padding: "8px 18px", fontSize: "13px", display: "flex", alignItems: "center", gap: "6px" }}
                  >
                    <ScienceIcon style={{ fontSize: 16 }} />
                    <span>Iniciar Molienda ({kgDisponibles.toLocaleString()} kg disp.)</span>
                  </button>
                );
              })()}
              {(selectedRecord.status === "borrador" || selectedRecord.status === "rechazada" || selectedRecord.status === "rechazado") && (
                <button
                  type="button"
                  onClick={() => {
                    const rec = selectedRecord;
                    setSelectedRecord(null);
                    handleEditRecord(rec);
                  }}
                  className="btn-add-new"
                  style={{ padding: "8px 16px", fontSize: "13px" }}
                >
                  <EditIcon style={{ fontSize: 16 }} />
                  <span>{selectedRecord.status === "rechazada" || selectedRecord.status === "rechazado" ? "Editar y Corregir" : "Editar Borrador"}</span>
                </button>
              )}
              {selectedRecord.status !== "cancelada" && selectedRecord.status !== "cancelado" && selectedRecord.status !== "aprobado" && selectedRecord.status !== "aceptada" && selectedRecord.status !== "aceptado" && handleCancelCoccion && (
                <button
                  type="button"
                  onClick={() => {
                    const rec = selectedRecord;
                    setSelectedRecord(null);
                    handleCancelCoccion(rec);
                  }}
                  className="btn-action-icon cancel"
                  style={{ width: "auto", padding: "8px 16px", height: "auto", borderRadius: "10px", fontWeight: 700, fontSize: "13px", display: "flex", gap: "6px", alignItems: "center" }}
                >
                  <CancelIcon style={{ fontSize: 16 }} />
                  <span>Cancelar</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => setSelectedRecord(null)}
                className="btn-secondary-custom"
                style={{ padding: "8px 20px", fontSize: "13px" }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}


      {/* =========================================================================
          MODAL 2: FORMULARIO NUEVA / EDITAR COCCIÓN (CON N TIPOS DE MAGUEY)
      ========================================================================= */}
      {isFormOpen && createPortal(
        <div
          className="modal-backdrop-custom"
          onClick={() => setIsFormOpen(false)}
        >
          <div
            className="modal-dialog-custom modal-xl animate-scaleIn"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "940px" }}
          >
            {/* Header del Modal */}
            <div className="modal-header-custom header-theme-coccion">
              <div className="modal-header-title">
                <div className="icon-badge">
                  <LocalFireDepartmentIcon />
                </div>
                <div>
                  <h3>{formData.rawId || formData.id ? "Editar / Corregir Bitácora de Cocción" : "Nueva Registro de Cocción"}</h3>
                  <p>Sección II: Registro de horneado, piñas e ingreso de especies de agave</p>
                </div>
              </div>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setIsFormOpen(false)}
                title="Cerrar ventana"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Cuerpo del Formulario */}
            <form
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                handleFormSubmit("enviada");
              }}
              className="modal-body-scrollable"
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              {/* Banner de Rechazo si está en corrección */}
              {(formData.status === "rechazada" || formData.status === "rechazado") && (
                <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: "14px", padding: "14px 18px", display: "flex", gap: "12px", alignItems: "flex-start" }}>
                  <ReportProblemIcon style={{ color: "#DC2626", marginTop: "2px" }} />
                  <div>
                    <h4 style={{ margin: "0 0 4px 0", color: "#991B1B", fontSize: "13.5px", fontWeight: 700 }}>
                      Bitácora Rechazada por el Área de Inspección
                    </h4>
                    <p style={{ margin: 0, color: "#7F1D1D", fontSize: "12.5px" }}>
                      <strong>Observaciones del inspector:</strong> {formData.observacionesCorreccion || formData.observaciones_correccion || "Por favor revise los datos del registro, realice las correcciones pertinentes y vuelva a enviar la solicitud para revisión."}
                    </p>
                  </div>
                </div>
              )}

              {/* Resumen Métrico de Totales en Tiempo Real */}
              <section
                className="detail-card"
                style={{
                  margin: 0,
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
                  gap: "12px",
                  padding: "14px 18px",
                  alignItems: "center",
                  background: "#FFFFFF",
                  border: "1px solid #E2E8F0",
                  borderRadius: "14px"
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 800, letterSpacing: "0.4px" }}>Especies</span>
                  <h4 style={{ margin: "3px 0 0 0", color: "var(--text-primary)", fontSize: "18px", fontWeight: 800 }}>
                    {formMagueyes.length} <small style={{ fontSize: "12px", color: "var(--text-muted)" }}>{formMagueyes.length === 1 ? 'especie' : 'especies'}</small>
                  </h4>
                </div>
                <div style={{ textAlign: "center" }}>
                  <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 800, letterSpacing: "0.4px" }}>Total Piñas</span>
                  <h4 style={{ margin: "3px 0 0 0", color: "var(--agave-green)", fontSize: "18px", fontWeight: 800 }}>
                    {formTotalPinas.toLocaleString()} <small style={{ fontSize: "12px", color: "var(--text-muted)" }}>pzas</small>
                  </h4>
                </div>
                <div style={{ textAlign: "center" }}>
                  <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 800, letterSpacing: "0.4px" }}>Total Crudo</span>
                  <h4 style={{ margin: "3px 0 0 0", color: "var(--agave-green)", fontSize: "18px", fontWeight: 800 }}>
                    {formTotalKgCrudo.toLocaleString()} <small style={{ fontSize: "12px", color: "var(--text-muted)" }}>kg</small>
                  </h4>
                </div>
                <div style={{ textAlign: "center" }}>
                  <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 800, letterSpacing: "0.4px" }}>Total Cocido</span>
                  <h4 style={{ margin: "3px 0 0 0", color: "var(--copper)", fontSize: "18px", fontWeight: 800 }}>
                    {formKgCocido ? `${formKgCocido.toLocaleString()} ` : '0 '}
                    <small style={{ fontSize: "12px", color: "var(--text-muted)" }}>kg</small>
                  </h4>
                </div>
                <div style={{ textAlign: "center" }}>
                  <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 800, letterSpacing: "0.4px" }}>Merma Est.</span>
                  <h4 style={{ margin: "3px 0 0 0", color: formMermaData ? "#D97706" : "var(--text-muted)", fontSize: "17px", fontWeight: 800 }}>
                    {formMermaData ? `${formMermaData.pctMerma}%` : '-'}
                  </h4>
                </div>
              </section>

              {/* Tarjeta 1: Materia Prima */}
              <section className="detail-card detail-card-accent-left-green" style={{ margin: 0 }}>
                <header className="detail-card-header">
                  <div className="detail-card-icon-wrapper-green">
                    <GrassIcon />
                  </div>
                  <div className="detail-card-title-wrapper">
                    <h2>1. Materia Prima & Procedencia</h2>
                    <p>Datos de cosecha, instalación y especies de agave ingresadas</p>
                  </div>
                </header>

                {/* Selección de Instalación */}
                <div className="integrated-input-box" style={{ marginBottom: "14px" }}>
                  <label className="integrated-input-label" htmlFor="idUnidadProduccion">
                    INSTALACIÓN (UNIDAD DE PRODUCCIÓN - ANEXO 1) *
                  </label>
                  <select
                    id="idUnidadProduccion"
                    name="idUnidadProduccion"
                    value={formData.idUnidadProduccion || ""}
                    onChange={handleChange}
                    className="integrated-select-control"
                    required
                  >
                    <option value="">-- Seleccionar Instalación / Unidad de Producción --</option>
                    {effectiveInstalaciones.map((inst) => {
                      const idAnexo = inst.idanexocliente || inst.id;
                      const infoGen = inst.informaciongeneralinstalacioncliente || inst;
                      const alias = infoGen?.alias || inst.alias || `Instalación #${idAnexo}`;

                      let dir = infoGen?.direccion;
                      if (!dir && infoGen) {
                        dir = [infoGen.calle, infoGen.numeroexterior || infoGen.numext, infoGen.colonia, infoGen.municipio, infoGen.estado].filter(Boolean).join(", ");
                      }
                      dir = dir || inst.direccion || "";

                      return (
                        <option key={idAnexo} value={idAnexo}>
                          {alias} {dir ? `— ${dir}` : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="species-grid" style={{ marginBottom: "14px" }}>
                  <div className="integrated-input-box">
                    <label className="integrated-input-label" htmlFor="noProduccion">
                      NO. DE PRODUCCIÓN (LOTE) *
                    </label>
                    <input
                      type="text"
                      id="noProduccion"
                      name="noProduccion"
                      value={formData.noProduccion || ""}
                      onChange={handleChange}
                      placeholder="Ej. PROD-2026-001"
                      className="integrated-input-control"
                      required
                    />
                  </div>

                  <div className="integrated-input-box">
                    <label className="integrated-input-label" htmlFor="fechaIngreso">
                      FECHA INGRESO MAGUEY
                    </label>
                    <input
                      type="date"
                      id="fechaIngreso"
                      name="fechaIngreso"
                      value={formData.fechaIngreso || ""}
                      onChange={handleChange}
                      className="integrated-input-control"
                    />
                  </div>
                </div>

                {/* APARTADO DINÁMICO DE N ESPECIES DE MAGUEY */}
                <div style={{ marginTop: "18px", marginBottom: "16px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", flexWrap: "wrap", gap: "10px" }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: "14.5px", fontWeight: 800, color: "var(--text-primary)" }}>
                        Especies de Maguey & Rendimiento ({formMagueyes.length})
                      </h3>
                      <p style={{ margin: "2px 0 0 0", fontSize: "11.5px", color: "var(--text-muted)" }}>
                        Agregue las especies de maguey a hornear con su número de piñas y peso individual
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={handleAddMaguey}
                      className="btn-add-new"
                      style={{ padding: "7px 16px", fontSize: "12px" }}
                    >
                      <AddIcon style={{ fontSize: 16 }} />
                      <span>+ Agregar Otra Especie</span>
                    </button>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                    {formMagueyes.map((m, index) => (
                      <div
                        key={m.id}
                        style={{
                          background: "#F8FAFC",
                          border: "1px solid #E2E8F0",
                          borderRadius: "14px",
                          padding: "14px 16px",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px", paddingBottom: "6px", borderBottom: "1px solid #E2E8F0" }}>
                          <span style={{ background: "rgba(74, 93, 35, 0.12)", color: "var(--agave-green)", fontWeight: 800, padding: "4px 12px", borderRadius: "8px", fontSize: "12px" }}>
                            Especie #{index + 1}
                          </span>

                          {formMagueyes.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveMaguey(m.id)}
                              title="Eliminar esta especie"
                              style={{ background: "#FEE2E2", color: "#DC2626", border: "1px solid #FCA5A5", borderRadius: "8px", padding: "4px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", fontSize: "11.5px", fontWeight: 700 }}
                            >
                              <DeleteOutlineIcon style={{ fontSize: 15 }} />
                              <span>Eliminar</span>
                            </button>
                          )}
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "1.35fr 0.85fr 0.85fr 0.95fr", gap: "10px", alignItems: "flex-start" }}>
                          {/* Tipo de Maguey Selector */}
                          <div className="integrated-input-box">
                            <label className="integrated-input-label">
                              TIPO DE MAGUEY (CATÁLOGO OFICIAL) *
                            </label>
                            <select
                              value={m.id_catalogo_maguey || m.idCatalogoMaguey || m.idMaguey || m.nc || ""}
                              onChange={e => {
                                const selId = Number(e.target.value);
                                const found = activeCatalog.find(c => Number(c.id_catalogo_maguey || c.idCatalogoMaguey || c.nc) === selId);
                                const fullName = found ? `${found.nombre_cientifico} (${found.nombre_comun})` : "";
                                const current = Array.isArray(formData?.magueyes) && formData.magueyes.length > 0 ? formData.magueyes : formMagueyes;
                                setFormData(prev => ({
                                  ...prev,
                                  magueyes: current.map(item => item.id === m.id ? {
                                    ...item,
                                    id_catalogo_maguey: selId,
                                    idCatalogoMaguey: selId,
                                    idMaguey: selId,
                                    nc: selId,
                                    tipoMaguey: fullName,
                                    nombreComun: found?.nombre_comun || "",
                                    nombreCientifico: found?.nombre_cientifico || "",
                                    edadMadurezAnios: found?.edad_madurez_anios || null
                                  } : item)
                                }));
                              }}
                              className="integrated-select-control"
                              required
                            >
                              <option value="">-- Seleccionar Variedad de Maguey --</option>
                              {activeCatalog.map((cat) => {
                                const catId = cat.id_catalogo_maguey || cat.idCatalogoMaguey || cat.nc;
                                return (
                                  <option key={catId} value={catId}>
                                    {catId}. {cat.nombre_comun} — {cat.nombre_cientifico}
                                  </option>
                                );
                              })}
                            </select>
                            {(() => {
                              const selId = m.id_catalogo_maguey || m.idCatalogoMaguey || m.idMaguey || m.nc;
                              const itemCat = activeCatalog.find(c => Number(c.id_catalogo_maguey || c.idCatalogoMaguey || c.nc) === Number(selId));
                              if (itemCat) {
                                return (
                                  <div style={{ marginTop: "4px", fontSize: "11px", color: "var(--text-muted)", display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                                    <span><strong>Científico:</strong> <em>{itemCat.nombre_cientifico}</em></span>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </div>

                          {/* Número de Piñas */}
                          <div className="integrated-input-box">
                            <label className="integrated-input-label">
                              NÚMERO DE PIÑAS *
                            </label>
                            <input
                              type="number"
                              min="1"
                              placeholder="Ej. 450"
                              value={m.numPinas || ""}
                              onChange={e => handleMagueyChange(m.id, 'numPinas', e.target.value)}
                              className="integrated-input-control"
                              style={{ paddingRight: "48px" }}
                              required
                            />
                            <span className="integrated-input-suffix">piñas</span>
                          </div>

                          {/* Kg de Maguey */}
                          <div className="integrated-input-box">
                            <label className="integrated-input-label">
                              KG DE MAGUEY CRUDO *
                            </label>
                            <input
                              type="number"
                              min="1"
                              placeholder="Ej. 9000"
                              value={m.kgMaguey || ""}
                              onChange={e => handleMagueyChange(m.id, 'kgMaguey', e.target.value)}
                              className="integrated-input-control"
                              style={{ paddingRight: "44px" }}
                              required
                            />
                            <span className="integrated-input-suffix">kg</span>
                          </div>

                          {/* N° de Guía / Predio */}
                          <div className="integrated-input-box">
                            <label className="integrated-input-label">
                              N° DE GUÍA / PREDIO (OPCIONAL)
                            </label>
                            <input
                              type="text"
                              placeholder="Ej. G-2024-001 (Opcional)"
                              value={(m.guiaPredio && m.guiaPredio.trim()) ? m.guiaPredio : ""}
                              onChange={e => handleMagueyChange(m.id, 'guiaPredio', e.target.value)}
                              className="integrated-input-control"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* % de ART en Maguey Crudo & N° de Informe */}
                <div className="species-grid">
                  <div className="integrated-input-box">
                    <label className="integrated-input-label" htmlFor="pctArt">
                      % DE ART EN MAGUEY CRUDO
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      id="pctArt"
                      name="pctArt"
                      value={formData.pctArt || ""}
                      onChange={handleChange}
                      placeholder="Ej. 24.5"
                      className="integrated-input-control"
                      style={{ paddingRight: "62px" }}
                    />
                    <span className="integrated-input-suffix">% ART</span>
                  </div>

                  <div className="integrated-input-box">
                    <label className="integrated-input-label" htmlFor="noInforme">
                      N° DE INFORME DE ENSAYO
                    </label>
                    <input
                      type="text"
                      id="noInforme"
                      name="noInforme"
                      value={formData.noInforme || ""}
                      onChange={handleChange}
                      placeholder="Ej. INF-COC-2026-081"
                      className="integrated-input-control"
                    />
                  </div>
                </div>
              </section>

              {/* Tarjeta 2: Proceso de Cocción */}
              <section className="detail-card detail-card-accent-left-copper" style={{ margin: 0 }}>
                <header className="detail-card-header">
                  <div className="detail-card-icon-wrapper-copper">
                    <LocalFireDepartmentIcon />
                  </div>
                  <div className="detail-card-title-wrapper">
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                      <h2>2. Proceso de Cocción</h2>
                      <span style={{ fontSize: "11px", fontWeight: 700, color: "#1E3A8A", background: "#DBEAFE", border: "1px solid #BFDBFE", padding: "2px 8px", borderRadius: "12px", textTransform: "none" }}>
                        Obligatorio para Envío Oficial
                      </span>
                    </div>
                    <p>Horneado del agave y pesaje cocido (puede guardarse como borrador durante el proceso o completarse para enviar a revisión)</p>
                  </div>
                </header>

                <div className="integrated-input-box" style={{ marginBottom: "14px" }}>
                  <label className="integrated-input-label" htmlFor="tipoHorno">
                    TIPO DE HORNO EMPLEADO *
                  </label>
                  <select
                    id="tipoHorno"
                    name="tipoHorno"
                    value={formData.tipoHorno || ""}
                    onChange={handleChange}
                    className="integrated-select-control"
                  >
                    <option value="">-- Seleccionar Tipo de Horno --</option>
                    {TIPOS_HORNO.map((tipo) => (
                      <option key={tipo} value={tipo}>
                        {tipo}
                      </option>
                    ))}
                    {formData.tipoHorno && !TIPOS_HORNO.includes(formData.tipoHorno) && (
                      <option value={formData.tipoHorno}>{formData.tipoHorno}</option>
                    )}
                  </select>
                </div>

                <div className="species-grid" style={{ marginBottom: "14px" }}>
                  <div className="integrated-input-box">
                    <label className="integrated-input-label" htmlFor="fechaInicioCoccion">
                      FECHA INICIAL DE COCCIÓN *
                    </label>
                    <input
                      type="date"
                      id="fechaInicioCoccion"
                      name="fechaInicioCoccion"
                      value={formData.fechaInicioCoccion || ""}
                      onChange={handleChange}
                      className="integrated-input-control"
                    />
                  </div>

                  <div className="integrated-input-box">
                    <label className="integrated-input-label" htmlFor="fechaFinCoccion">
                      FECHA FINAL DE COCCIÓN *
                    </label>
                    <input
                      type="date"
                      id="fechaFinCoccion"
                      name="fechaFinCoccion"
                      value={formData.fechaFinCoccion || ""}
                      onChange={handleChange}
                      className="integrated-input-control"
                    />
                  </div>
                </div>

                <div className="integrated-input-box" style={{ marginBottom: "14px" }}>
                  <label className="integrated-input-label" htmlFor="kgMagueyCocido">
                    KG DE MAGUEY COCIDO OBTENIDO *
                  </label>
                  <input
                    type="number"
                    id="kgMagueyCocido"
                    name="kgMagueyCocido"
                    value={formData.kgMagueyCocido || ""}
                    onChange={handleChange}
                    placeholder="Ej. 8200"
                    className="integrated-input-control"
                    style={{ paddingRight: "46px" }}
                  />
                  <span className="integrated-input-suffix">kg</span>
                </div>

                <div className="integrated-input-box">
                  <label className="integrated-input-label" htmlFor="observaciones">
                    OBSERVACIONES GENERALES
                  </label>
                  <textarea
                    id="observaciones"
                    name="observaciones"
                    value={formData.observaciones || ""}
                    onChange={handleChange}
                    placeholder="Observaciones adicionales, leña utilizada, contingencias en el horneado, etc."
                    className="integrated-input-control"
                    style={{ minHeight: "50px", resize: "none", marginTop: "2px" }}
                  />
                </div>
              </section>

              {/* Botones del Modal */}
              <div className="modal-footer-custom" style={{ marginTop: "8px", borderRadius: "12px" }}>
                <button
                  type="button"
                  className="btn-cancel-modal"
                  onClick={() => setIsFormOpen(false)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn-secondary-custom"
                  style={{ padding: "9px 18px", fontSize: "12.5px" }}
                  onClick={() => handleFormSubmit("borrador")}
                >
                  Guardar Borrador
                </button>
                <button
                  type="button"
                  className="btn-submit-modal btn-coccion-submit"
                  onClick={() => handleFormSubmit("enviada")}
                >
                  <span>Firmar y Enviar Cocción ({formMagueyes.length} {formMagueyes.length === 1 ? 'Especie' : 'Especies'})</span>
                  <ArrowForwardIcon style={{ fontSize: 16 }} />
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CoccionSection;
